import React, { useEffect, useState } from "react";
import api from "../../../../services/api";
import StatusCliente from "../statusCliente";

/**
 * Conteúdo da aba "Tráfego" do painel direito (detalhe da CPE).
 * Faz a busca de interfaces e séries temporais do host selecionado e
 * renderiza os gráficos via StatusCliente.
 *
 * `selectedRow` vive no index.js (definido pelo datagrid de clientes da
 * coluna esquerda) e é consumido aqui.
 */
export default function TabTrafego({
	selectedRow,
	initial_date,
	final_date,
	controllerDashboard,
	setControllerDashboard,
	setSnackbar,
}) {
	const [selectedInterface, setSelectedInterface] = useState(null);
	const [allInterfaces, setAllInterfaces] = useState([]);
	const [loadingInterface, setLoadingInterface] = useState(false);
	const [statusInterface, setStatusInterface] = useState(null);
	const [errorPacketCount, setErrorPacketCount] = useState({ in: 0, out: 0 });
	const [networkSummary, setNetworkSummary] = useState({ in: [], out: [] });
	const [loadingCharts, setLoadingCharts] = useState({
		in: false,
		out: false,
		erroPacketIn: false,
		erroPacketOut: false,
		statusInterface: false,
		inSpeed: false,
		outSpeed: false,
	});
	const [controller, setController] = useState(null);

	// Carregamento de dados de interfaces
	useEffect(() => {
		if (!!selectedRow) {
			setNetworkSummary({ in: [], out: [] });
			setStatusInterface(null);
			getInterface();
		}
	}, [selectedRow]);

	// Carregamento de dados dos gráficos
	useEffect(() => {
		if (!!selectedRow && allInterfaces.length > 0) {
			setNetworkSummary({ in: [], out: [] });
			setStatusInterface(null);
			fetchData();
		}
	}, [allInterfaces, initial_date]);

	// Recebe a lista de interfaces do host selecionado
	const getInterface = async () => {
		setLoadingInterface(true);
		setAllInterfaces([]);
		setSelectedInterface(null);

		try {
			const response = await api.post("/zabbix/interfaceList", {
				hostId: selectedRow?.hostId,
			});
			setSelectedInterface(response?.data?.slice(0, 10)?.map((i) => i?.name));
			setAllInterfaces(response?.data || []);
		} catch (error) {
			console.error("Error fetching hosts:", error);
			setSnackbar({ children: "Erro ao buscar interfaces", severity: "error" });
		} finally {
			setLoadingInterface(false);
		}
	};

	// Requisição dos dados das dashs
	const fetchData = async () => {
		if (controller) controller.abort();
		if (controllerDashboard) controllerDashboard.abort();

		const newController = new AbortController();
		const signal = newController.signal;
		setControllerDashboard(newController);
		setController(newController);

		try {
			await Promise.all([
				apiHandleTrafficIn(signal),
				apiHandleTrafficOut(signal),
				getErrorPacketIn(signal),
				getErrorPacketOut(signal),
				getStatusInterface(signal),
				apiHandleTrafficInSpeed(signal),
				apiHandleTrafficOutSpeed(signal),
			]);
		} catch (error) {
			console.error(error);
			if (error.name !== "CanceledError") {
				setSnackbar({
					children: `Error:${error?.message || "Erro durante a solicitação de rotas"}`,
					severity: "error",
				});
			}
		}
	};

	async function apiHandleTrafficIn(signal) {
		setLoadingCharts((prev) => ({ ...prev, in: true }));
		try {
			const response = await api.post(
				"/zabbix/serieTemporal",
				{
					initial_date: initial_date,
					final_date: final_date,
					hostids: selectedRow?.hostId,
					itemsKeys: "ifHCInOctets",
					interface: selectedInterface,
					stackedData: true,
				},
				{ signal },
			);

			if (response.data.status !== "Error" && response?.data) {
				setNetworkSummary((prev) => ({ ...prev, in: response?.data || [] }));
			}
		} catch (error) {
			if (error.name !== "CanceledError") console.error(error);
		} finally {
			setLoadingCharts((prev) => ({ ...prev, in: false }));
		}
	}

	async function apiHandleTrafficInSpeed(signal) {
		setLoadingCharts((prev) => ({ ...prev, inSpeed: true }));
		try {
			const response = await api.post(
				"/zabbix/serieTemporal",
				{
					initial_date: initial_date,
					final_date: final_date,
					hostids: selectedRow?.hostId,
					interface: selectedInterface,
					itemsKeys: "ifHCInOctets",
					lastItem: true,
				},
				{ signal },
			);

			if (response.data.status !== "Error" && response?.data) {
				setNetworkSummary((prev) => ({
					...prev,
					inSpeed: response?.data || {},
				}));
			}
		} catch (error) {
			if (error.name !== "CanceledError") console.error(error);
		} finally {
			setLoadingCharts((prev) => ({ ...prev, inSpeed: false }));
		}
	}

	async function apiHandleTrafficOut(signal) {
		setLoadingCharts((prev) => ({ ...prev, out: true }));
		try {
			const response = await api.post(
				"/zabbix/serieTemporal",
				{
					initial_date: initial_date,
					final_date: final_date,
					hostids: selectedRow?.hostId,
					itemsKeys: "ifHCOutOctets",
					interface: selectedInterface,
					stackedData: true,
				},
				{ signal },
			);

			if (response.data.status !== "Error" && response?.data) {
				setNetworkSummary((prev) => ({ ...prev, out: response?.data || [] }));
			}
		} catch (error) {
			if (error.name !== "CanceledError") console.error(error);
		} finally {
			setLoadingCharts((prev) => ({ ...prev, out: false }));
		}
	}

	async function apiHandleTrafficOutSpeed(signal) {
		setLoadingCharts((prev) => ({ ...prev, outSpeed: true }));
		try {
			const response = await api.post(
				"/zabbix/serieTemporal",
				{
					initial_date: initial_date,
					final_date: final_date,
					hostids: selectedRow?.hostId,
					interface: selectedInterface,
					itemsKeys: "ifHCOutOctets",
					lastItem: true,
				},
				{ signal },
			);

			if (response.data.status !== "Error" && response?.data) {
				setNetworkSummary((prev) => ({
					...prev,
					outSpeed: response?.data || [],
				}));
			}
		} catch (error) {
			if (error.name !== "CanceledError") console.error(error);
		} finally {
			setLoadingCharts((prev) => ({ ...prev, outSpeed: false }));
		}
	}

	const getErrorPacketIn = async (signal) => {
		setErrorPacketCount((prev) => ({ ...prev, in: 0 }));
		setLoadingCharts((prev) => ({ ...prev, erroPacketIn: true }));
		try {
			const response = await api.post(
				"/zabbix/serieTemporal",
				{
					initial_date: initial_date,
					final_date: final_date,
					interface: selectedInterface,
					hostids: selectedRow?.hostId,
					itemsKeys: "net.if.in.errors",
					lastItem: true,
				},
				{ signal },
			);

			setErrorPacketCount((prev) => ({
				...prev,
				in: response?.data.value || 0,
			}));
		} catch (error) {
			if (error.name !== "CanceledError") console.error(error);
		} finally {
			setLoadingCharts((prev) => ({ ...prev, erroPacketIn: false }));
		}
	};

	const getErrorPacketOut = async (signal) => {
		setErrorPacketCount((prev) => ({ ...prev, out: 0 }));
		setLoadingCharts((prev) => ({ ...prev, erroPacketOut: true }));
		try {
			const response = await api.post(
				"/zabbix/serieTemporal",
				{
					initial_date: initial_date,
					final_date: final_date,
					interface: selectedInterface,
					hostids: selectedRow?.hostId,
					itemsKeys: "net.if.out.errors",
					lastItem: true,
				},
				{ signal },
			);

			setErrorPacketCount((prev) => ({
				...prev,
				out: response?.data.value || 0,
			}));
		} catch (error) {
			if (error.name !== "CanceledError") console.error(error);
		} finally {
			setLoadingCharts((prev) => ({ ...prev, erroPacketOut: false }));
		}
	};

	const getStatusInterface = async (signal) => {
		setLoadingCharts((prev) => ({ ...prev, statusInterface: true }));
		try {
			const response = await api.post(
				"/zabbix/serieTemporal",
				{
					interface: selectedInterface,
					hostids: selectedRow?.hostId,
					itemsKeys: "ifOperStatus",
					lastItem: true,
				},
				{ signal },
			);

			const responseKeys = Object.keys(response?.data);
			const up = [];
			const down = [];
			responseKeys?.forEach((key) => {
				if (response.data[key].value === "1") {
					up.push({ [key]: response?.data[key] });
				} else {
					down.push({ [key]: response?.data[key] });
				}
			});
			setStatusInterface({ up, down });
		} catch (error) {
			if (error.name !== "CanceledError") console.error(error);
		} finally {
			setLoadingCharts((prev) => ({ ...prev, statusInterface: false }));
		}
	};

	return (
		<StatusCliente
			errorPacketCount={errorPacketCount}
			networkSummary={networkSummary}
			selectedRow={selectedRow}
			allInterfaces={allInterfaces}
			loadingCharts={loadingCharts}
			statusInterface={statusInterface}
			loadingInterface={loadingInterface}
			selectedInterface={selectedInterface}
			setSelectedInterface={setSelectedInterface}
			height="100%"
		/>
	);
}
